import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { Loader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", start_date: "", end_date: "", schedule: "" };

export default function Batches() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["batches"], queryFn: async () => (await api.get("/batches")).data });

  const save = async () => {
    if (!form.name) return toast.error("Batch name required");
    try {
      if (editId) await api.put(`/batches/${editId}`, form); else await api.post("/batches", form);
      toast.success("Batch saved!"); setOpen(false); setForm(empty); setEditId(null);
      qc.invalidateQueries({ queryKey: ["batches"] });
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { await api.delete(`/batches/${id}`); qc.invalidateQueries({ queryKey: ["batches"] }); toast.success("Deleted"); };

  if (isLoading) return <Loader />;

  return (
    <div className="space-y-5" data-testid="batches-page">
      <div className="flex items-center justify-between">
        <h2 className="font-head text-3xl text-csh-blue">Batches 📦</h2>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditId(null); } }}>
          <DialogTrigger asChild><Button className="rounded-full gradient-purple text-white" data-testid="add-batch-btn"><Icons.Plus size={18} /> New Batch</Button></DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader><DialogTitle className="font-head text-2xl text-csh-blue">{editId ? "Edit" : "New"} Batch</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Batch name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border-2 py-6" data-testid="batch-name" />
              <div className="grid grid-cols-2 gap-3">
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-xl border-2 py-6" />
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-xl border-2 py-6" />
              </div>
              <Input placeholder="Schedule e.g. Tue 5PM & Sat 4:30PM" value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} className="rounded-xl border-2 py-6" />
              <Button onClick={save} className="w-full rounded-full gradient-orange py-6 font-bold text-white" data-testid="save-batch-btn">Save Batch</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {(!data || data.length === 0) ? <EmptyState icon="Boxes" title="No batches" /> : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {data.map((b) => (
            <div key={b.id} className="rounded-3xl bg-white p-5 shadow-sm border border-black/5" data-testid={`batch-${b.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl gradient-blue"><Icons.Boxes size={20} className="text-white" /></div>
                <button onClick={() => del(b.id)} className="rounded-lg p-2 hover:bg-black/5"><Icons.Trash2 size={16} className="text-red-500" /></button>
              </div>
              <h3 className="mt-3 font-head text-xl text-csh-blue">{b.name}</h3>
              <p className="text-sm text-muted-foreground">{b.schedule}</p>
              <div className="mt-2 flex items-center gap-2 text-sm font-bold text-csh-purple"><Icons.Users size={15} /> {b.student_count} students</div>
              <p className="mt-1 text-xs text-muted-foreground">{b.start_date} → {b.end_date}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
